import { useMemo } from 'react';

import { StyleSheet, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
} from 'react-native-reanimated';

import { ConfettiAnimation } from 'pages/cart/cart-item/redeem-reward/confetti-animation';
import type { Tier } from 'pages/loyalty/loyalty-profile/profile-card/components/hooks/use-progress-calculation';
import { primitive } from 'styles/constants/primitives';

import { ProgressMarkers, markerWidth } from './progress-markers';

interface ProgressBarProps {
  tiers: Tier[];
  widthSV: any;
  showConfettiAnimation: boolean;
  currentPoints: number;
}

// UI settings
const progressBarRadius = 8;
const markerPaddingLeft = 20;
const markerPaddingRight = 20;
const totalProgressPadding = markerPaddingLeft + markerPaddingRight;
const confettiOffset = 38;

export const ProgressBar = ({
  tiers,
  widthSV,
  showConfettiAnimation,
  currentPoints,
}: ProgressBarProps) => {
  const containerW = useSharedValue(0);

  const isBelowFirstTier = useMemo(() => currentPoints < tiers[0].points, [currentPoints, tiers]);
  const isOverLastTier = useMemo(() => currentPoints >= tiers[tiers.length - 1].points, [
    currentPoints,
    tiers,
  ]);
  // Due to the shape of the progress bar, and the markers position, we need to add an extra width to complete the bar
  // when the user has same or more points than the last tier
  const additionToProgress = useMemo(() => (isOverLastTier ? markerPaddingLeft : markerWidth / 2), [
    isOverLastTier,
  ]);

  // Derived values
  const translateX = useDerivedValue(() => {
    'worklet';
    const contentW = containerW.value - totalProgressPadding;

    // Don't show bar until we have real width measurement
    if (contentW <= 0) {
      return -9999;
    }

    const pct = Math.max(0, Math.min(1, widthSV.value / 100));

    if (isBelowFirstTier) {
      return -containerW.value + markerPaddingRight + markerPaddingLeft * pct;
    }

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    return lerp(-contentW, 0, pct) + additionToProgress;
  });

  const confettiTranslateX = useDerivedValue(() => {
    'worklet';
    return translateX.value - confettiOffset;
  });

  // Animated styles
  const fillStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }));

  const confettiStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: confettiTranslateX.value }],
  }));

  return (
    <View style={styles.progressBarContainer}>
      {currentPoints > 0 && (
        <>
          <View
            style={styles.progressBarFillContainer}
            onLayout={({
              nativeEvent: {
                layout: { width },
              },
            }) => {
              containerW.value = width;
            }}
          >
            <Animated.View style={[styles.progressBarFill, fillStyle]}>
              <View style={styles.progressBarFillStart} />
            </Animated.View>
          </View>
          {showConfettiAnimation && (
            <View style={styles.confettiContainer}>
              <Animated.View style={[styles.confettiWrapper, confettiStyle]}>
                <ConfettiAnimation />
              </Animated.View>
            </View>
          )}
        </>
      )}
      <View style={styles.progressBarTrack}>
        <ProgressMarkers tiers={tiers} currentPoints={currentPoints} />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  progressBarContainer: {
    marginBottom: 2,
    backgroundColor: primitive.$blackOpacity10,
    paddingRight: markerPaddingRight,
    paddingLeft: markerPaddingLeft,
    borderRadius: progressBarRadius,
  },
  progressBarTrack: { position: 'relative', height: 16 },
  progressBarFillContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    paddingRight: markerPaddingRight,
    paddingLeft: markerPaddingLeft,
    height: '100%',
    borderRadius: progressBarRadius,
    overflow: 'hidden',
  },
  progressBarFillStart: {
    position: 'absolute',
    top: 0,
    left: -totalProgressPadding,
    width: totalProgressPadding,
    height: '100%',
    backgroundColor: primitive.bk.$meltyYellow,
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: primitive.bk.$meltyYellow,
    width: '100%',
    borderTopRightRadius: progressBarRadius,
    borderBottomRightRadius: progressBarRadius,
  },
  confettiContainer: {
    position: 'absolute',
    top: 0,
    left: markerPaddingLeft,
    right: markerPaddingRight,
    bottom: 0,
  },
  confettiWrapper: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: 10,
    justifyContent: 'center',
  },
});
